import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { BottomNav } from "@/components/BottomNav";
import { AuthProvider, useAuth } from "@/lib/auth";
import Home from "@/pages/Home";
import JobReview from "@/pages/JobReview";
import InvoicePreview from "@/pages/InvoicePreview";
import History from "@/pages/History";
import Clients from "@/pages/Clients";
import Settings from "@/pages/Settings";
import SharePage from "@/pages/SharePage";
import EstimatePreview from "@/pages/EstimatePreview";
import EstimateShare from "@/pages/EstimateShare";
import Schedule from "@/pages/Schedule";
import JobWonBanner from "@/components/JobWonBanner";
import Onboarding from "@/pages/Onboarding";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Receipts from "@/pages/Receipts";
import ReceiptScan from "@/pages/ReceiptScan";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Stock from "@/pages/Stock";
import Paywall from "@/pages/Paywall";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Login";
import Landing from "@/pages/Landing";

const OPEN_EXACT = new Set(["/login", "/register", "/forgot", "/reset"]);
const LEGAL_EXACT = new Set(["/privacy", "/terms"]);

function isPublicPath(path) {
  return (
    path === "/" ||
    OPEN_EXACT.has(path) ||
    LEGAL_EXACT.has(path) ||
    path.startsWith("/share/") ||
    path.startsWith("/e/") ||
    path.startsWith("/payment")
  );
}

function allowsUnpaid(path) {
  // Marketing "/" is public for guests, but entitled-app Home still requires a paid/trial user.
  return (
    OPEN_EXACT.has(path) ||
    LEGAL_EXACT.has(path) ||
    path.startsWith("/share/") ||
    path.startsWith("/e/") ||
    path.startsWith("/payment") ||
    path === "/paywall" ||
    path === "/settings" ||
    path.startsWith("/billing")
  );
}

function Shell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const path = location.pathname;

  useEffect(() => {
    const onPaywall = () => {
      if (path !== "/paywall" && path !== "/settings") nav("/paywall");
    };
    const onUnauthorized = () => {
      if (!isPublicPath(path)) nav("/login");
    };
    window.addEventListener("js-paywall", onPaywall);
    window.addEventListener("js-unauthorized", onUnauthorized);
    return () => {
      window.removeEventListener("js-paywall", onPaywall);
      window.removeEventListener("js-unauthorized", onUnauthorized);
    };
  }, [nav, path]);

  useEffect(() => {
    let listener;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import("@capacitor/app");
        listener = await App.addListener("appUrlOpen", (event) => {
          try {
            const url = new URL(event.url);
            const next = url.protocol === "jobspoken:"
              ? `/${url.host}${url.pathname === "/" ? "" : url.pathname}`
              : url.pathname;
            nav(`${next}${url.search}`);
          } catch {
            /* ignore malformed links */
          }
        });
      } catch {
        /* browser build */
      }
    })();
    return () => {
      if (listener && typeof listener.remove === "function") listener.remove();
    };
  }, [nav]);

  if (loading) {
    return <div className="p-10 text-center text-zinc-400">Loading Job Spoken…</div>;
  }
  if (!user && !isPublicPath(path)) return <Navigate to="/login" replace />;
  if (user && OPEN_EXACT.has(path)) return <Navigate to={user.entitled ? "/" : "/paywall"} replace />;
  if (user && !user.entitled && !allowsUnpaid(path)) return <Navigate to="/paywall" replace />;

  const showNav = user && user.entitled && !path.startsWith("/share/") && !path.startsWith("/e/") && !path.startsWith("/payment") && path !== "/onboarding";
  const isMarketing = !user && (path === "/" || LEGAL_EXACT.has(path));

  if (isMarketing) {
    return (
      <>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<LegalRedirect file="/privacy.html" />} />
          <Route path="/terms" element={<LegalRedirect file="/terms.html" />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <>
      <div className="w-full max-w-md mx-auto min-h-screen bg-zinc-50 relative pb-24 shadow-2xl overflow-x-hidden">
        {user && user.entitled && <JobWonBanner />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/job/:id" element={<JobReview />} />
          <Route path="/invoice/:id" element={<InvoicePreview />} />
          <Route path="/estimate/:id" element={<EstimatePreview />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/history" element={<History />} />
          <Route path="/receipts" element={<Receipts />} />
          <Route path="/receipt/scan" element={<ReceiptScan />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/stock" element={<Stock />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/share/:shareId" element={<SharePage />} />
          <Route path="/e/:token" element={<EstimateShare />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/paywall" element={<Paywall />} />
          <Route path="/billing/success" element={<BillingReturn />} />
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
        </Routes>
        {showNav && <BottomNav />}
      </div>
      <Toaster position="top-center" richColors />
    </>
  );
}

function BillingReturn() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    let tries = 0;
    const tick = async () => {
      const next = await refresh();
      tries += 1;
      if (next?.entitled || tries > 5) nav(next?.entitled ? "/" : "/paywall");
      else setTimeout(tick, 1500);
    };
    tick();
  }, [refresh, nav]);
  return <div className="p-10 text-center text-zinc-500">Confirming your subscription…</div>;
}

function LegalRedirect({ file }) {
  useEffect(() => {
    window.location.replace(file);
  }, [file]);
  return <div className="p-10 text-center text-zinc-400">Opening…</div>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
